import { Bold, Italic, Strikethrough } from 'lucide-react';
import { cn } from '@/lib/utils';

type FormatStyle = 'bold' | 'italic' | 'strike';

interface TextFormattingToolbarProps {
  visible: boolean;
  position: { x: number; y: number };
  onFormat: (style: FormatStyle) => void;
}

const FORMAT_BUTTONS: { style: FormatStyle; icon: typeof Bold; label: string }[] = [
  { style: 'bold', icon: Bold, label: 'Bold (*text*)' },
  { style: 'italic', icon: Italic, label: 'Italic (_text_)' },
  { style: 'strike', icon: Strikethrough, label: 'Strikethrough (~text~)' },
];

/**
 * Floating formatting toolbar that appears when text is selected
 * in a message textarea. Provides Bold, Italic, Strikethrough buttons
 * using WhatsApp-style markers.
 */
export function TextFormattingToolbar({
  visible,
  position,
  onFormat,
}: TextFormattingToolbarProps) {
  if (!visible) return null;

  return (
    <div
      data-formatting-toolbar
      className={cn(
        'fixed z-[100] flex items-center gap-0.5 rounded-lg border border-border bg-popover px-1 py-0.5 shadow-lg',
        'animate-in fade-in-0 zoom-in-95 duration-100'
      )}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -100%)',
      }}
    >
      {FORMAT_BUTTONS.map(({ style, icon: Icon, label }) => (
        <button
          key={style}
          type="button"
          title={label}
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent textarea blur
            onFormat(style);
          }}
          className={cn(
            'rounded p-1.5 text-muted-foreground transition-colors',
            'hover:bg-muted hover:text-foreground'
          )}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
}
